import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { Upload, FileText, Trash2, X, AlertCircle, Edit2 } from 'lucide-react';
import '../styles/FilesPanelSimple.css';

interface FileItem {
  id: string;
  file_name: string;
  file_size: number;
  file_type: string;
  storage_path: string;
  file_order: number;
  created_at: string;
  estimation_id: string;
  url?: string;
}

interface FilesPanelSimpleProps {
  estimationId: string;
  onFileSelect?: (file: FileItem | null) => void;
  currentFile?: FileItem | null;
  onCollapse?: () => void;
  projectName?: string;
}

export const FilesPanelSimple: React.FC<FilesPanelSimpleProps> = ({
  estimationId,
  onFileSelect,
  currentFile,
  onCollapse,
  projectName
}) => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(currentFile?.id || null);
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const [editingFileName, setEditingFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  // Fetch files
  const fetchFiles = async () => {
    try {
      const { data, error } = await supabase
        .from('estimation_files')
        .select('*')
        .eq('estimation_id', estimationId)
        .order('file_order', { ascending: true });

      if (error) throw error;

      const filesWithUrls = (data || []).map(file => {
        const fullUrl = file.storage_path?.startsWith('http')
          ? file.storage_path
          : `https://fqlgzaymblosubgkkeos.supabase.co/storage/v1/object/public/bucket/${file.storage_path}`;

        return {
          ...file,
          url: fullUrl,
          storage_path: fullUrl
        };
      });

      setFiles(filesWithUrls);

      // Auto-select first file if none selected
      if (!selectedFileId && filesWithUrls.length > 0) {
        const firstFile = filesWithUrls[0];
        setSelectedFileId(firstFile.id);
        onFileSelect?.(firstFile);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      setError('Failed to load files');
    }
  };

  useEffect(() => {
    fetchFiles();

    const subscription = supabase
      .channel(`estimation-files-${estimationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'estimation_files',
          filter: `estimation_id=eq.${estimationId}`
        },
        () => {
          fetchFiles();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [estimationId]);

  // Handle file upload
  const handleFileUpload = async (fileList: FileList | File[]) => {
    const filesArray = Array.from(fileList);
    if (filesArray.length === 0) return;

    // Check if all files are PDFs
    const nonPdfFiles = filesArray.filter(file => file.type !== 'application/pdf');
    if (nonPdfFiles.length > 0) {
      setError('Please upload only PDF files');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const { data: existingFiles } = await supabase
        .from('estimation_files')
        .select('file_order')
        .eq('estimation_id', estimationId)
        .order('file_order', { ascending: false })
        .limit(1);

      let nextOrder = existingFiles && existingFiles.length > 0 ? (existingFiles[0].file_order || 0) + 1 : 0;

      const uploadPromises = filesArray.map(async (file, index) => {
        const timestamp = Date.now();
        const randomStr = Math.random().toString(36).substring(7);
        const fileName = `blueprints/${timestamp}_${randomStr}.pdf`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('bucket')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        const { data: fileRecord, error: dbError } = await supabase
          .from('estimation_files')
          .insert({
            estimation_id: estimationId,
            file_name: file.name,
            file_size: file.size,
            file_type: file.type,
            storage_path: fileName,
            file_order: nextOrder + index
          })
          .select()
          .single();

        if (dbError) throw dbError;

        setUploadProgress(((index + 1) / filesArray.length) * 100);
        return fileRecord;
      });

      const uploadedFiles = await Promise.all(uploadPromises);

      if (uploadedFiles.length > 0) {
        const firstFile = uploadedFiles[0];
        setSelectedFileId(firstFile.id);
        onFileSelect?.(firstFile);
      }

      setUploadProgress(100);
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 500);
    } catch (error) {
      console.error('Error uploading files:', error);
      setError('Failed to upload files');
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle file selection
  const handleFileSelect = (file: FileItem) => {
    setSelectedFileId(file.id);
    onFileSelect?.(file);
  };

  // Handle file name edit
  const handleStartEdit = (file: FileItem) => {
    setEditingFileId(file.id);
    // Remove .pdf extension for editing
    const nameWithoutExt = file.file_name.replace(/\.pdf$/i, '');
    setEditingFileName(nameWithoutExt);
    setTimeout(() => editInputRef.current?.focus(), 0);
  };

  const handleSaveEdit = async (fileId: string) => {
    if (!editingFileName.trim()) {
      setEditingFileId(null);
      return;
    }

    // Ensure .pdf extension
    const finalFileName = editingFileName.endsWith('.pdf') 
      ? editingFileName 
      : `${editingFileName}.pdf`;

    try {
      const { error } = await supabase
        .from('estimation_files')
        .update({ file_name: finalFileName })
        .eq('id', fileId);

      if (error) throw error;

      // Update local state
      setFiles(files.map(f => 
        f.id === fileId ? { ...f, file_name: finalFileName } : f
      ));

      setEditingFileId(null);
    } catch (error) {
      console.error('Error updating file name:', error);
      setError('Failed to update file name');
    }
  };

  const handleCancelEdit = () => {
    setEditingFileId(null);
    setEditingFileName('');
  };

  // Handle file deletion
  const handleFileDelete = async (fileId: string, storagePath: string) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      const urlParts = storagePath.split('/');
      const fileName = `blueprints/${urlParts[urlParts.length - 1]}`;

      await supabase.storage.from('bucket').remove([fileName]);

      const { error: dbError } = await supabase
        .from('estimation_files')
        .delete()
        .eq('id', fileId);

      if (dbError) throw dbError;

      const newFiles = files.filter(f => f.id !== fileId);
      setFiles(newFiles);

      if (selectedFileId === fileId) {
        const nextFile = newFiles[0] || null;
        setSelectedFileId(nextFile?.id || null);
        onFileSelect?.(nextFile);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      setError('Failed to delete file');
    }
  };

  return (
    <div className="files-panel-simple">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        multiple
        onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
        disabled={isUploading}
        style={{ display: 'none' }}
      />

      {/* Project Name */}
      {projectName && (
        <div className="files-project-name">
          <h3>{projectName}</h3>
        </div>
      )}

      {/* Upload Button at Top */}
      <div className="files-simple-upload-top">
        <button
          className="upload-btn-top"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          <Upload size={16} />
          <span>Upload PDF</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="files-simple-error">
          <AlertCircle size={16} />
          {error}
          <button onClick={() => setError(null)} className="error-close">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Upload Progress */}
      {isUploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
          </div>
          <span className="progress-text">Uploading... {Math.round(uploadProgress)}%</span>
        </div>
      )}

      {/* Files List */}
      <div className="files-simple-list">
        {files.length === 0 ? (
          <div className="files-simple-empty">
            <FileText size={48} className="empty-icon" />
            <p>No files yet</p>
            <button className="empty-upload-btn" onClick={() => fileInputRef.current?.click()}>
              <Upload size={16} />
              Upload PDF
            </button>
          </div>
        ) : (
          files.map((file, index) => (
            <div
              key={file.id}
              className={`file-simple-item ${selectedFileId === file.id ? 'selected' : ''} ${editingFileId === file.id ? 'editing' : ''}`}
              onClick={() => !editingFileId && handleFileSelect(file)}
            >
              <div className="file-simple-index">{index + 1}</div>
              <div className="file-simple-info">
                {editingFileId === file.id ? (
                  <input
                    ref={editInputRef}
                    type="text"
                    value={editingFileName}
                    onChange={(e) => setEditingFileName(e.target.value)}
                    onBlur={() => handleSaveEdit(file.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveEdit(file.id);
                      } else if (e.key === 'Escape') {
                        handleCancelEdit();
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="file-name-edit-input"
                  />
                ) : (
                  <div className="file-simple-name">
                    {file.file_name}
                  </div>
                )}
              </div>
              <button
                className="file-simple-edit"
                onClick={(e) => {
                  e.stopPropagation();
                  if (editingFileId === file.id) {
                    handleSaveEdit(file.id);
                  } else {
                    handleStartEdit(file);
                  }
                }}
                title={editingFileId === file.id ? "Save" : "Edit"}
              >
                <Edit2 size={14} />
              </button>
              <button
                className="file-simple-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFileDelete(file.id, file.storage_path || file.url || '');
                }}
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FilesPanelSimple;

